import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../../../../../app/hooks';
import { exportRawFromUnderwritingAndPolus } from '../../../../../../../../store/company/apiHelpers';
import {
    handleDealApplicationDrawer,
    setComposeEmailModal,
    setCustomFilterDrawer,
    setDealDrawer,
    setConfigTableModal,
    updateDeal,
    updateInvestorDeal,
    setDealTermsLoggingDrawerPrefilledStatus,
    fetchLenderConfig,
} from '../../../../../../../../store/investor/action';
import {
    DEAL,
    DealStatus,
    DealStatusConfig,
    FetchDealsData,
    InvestorDealData,
    MANDATE,
} from '../../../../../../../../store/investor/reducer';
import { ctaGrey, GREEN_DROPDOWN_CARET, GREY_DROPDOWN_CARET, RED_DROPDOWN_CARET } from '../../../../../../../../assets/hostedassets';
import styles from './DealsRow.module.scss';
import { USER_TYPE } from '../../../../../../../../utils/constants/user';
import ActionMenu from '../../../../../../../../common/ActionMenu/ActionMenu';
import { StatusDropdownOption } from '../../../../../../../../common/_custom/StatusDropdown/StatusDropdown';
import { downloadFile } from '../../../../../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { UPLOAD_FILES_PREFIXES } from '../../../../../../../../common/ManageDealDrawer/DealDrawerConstants';
import CaretIcon from '../../../../../../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../../../../../../utils/constants/colors';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import TopRightArrowIcon from '../../../../../../../../assets/Svg/TopRightArrowIcon';
import {
    createDropdownOptions,
    getPlatformUserId,
    getSubdomain,
    isLenderType,
    openUserSummonTab,
} from '../../../../../../../../utils/utils';
import { getStatusDropdownOption as getStatusDropdownOptionUtil } from '../../../../../../../../utils/commonUtil';
import { COMPOSE_EMAIL_SOURCE, SOURCE } from '../../../../../../../../common/NeedInfoV2/NeedInfoV2Constants';
import {
    DEAL_STATUS_COUNT_TYPES,
    DEAL_VIEW_TYPES,
    USER_TYPES,
    ENTITY_TYPES,
    DEAL_COLUMN_KEYS,
} from '../../../../../../../../store/investor/constants';
import { getOrgBasedUsers } from '../../../../../../../../store/investor/apiHelpers';
import {
    fetchInternalSummonAicaData,
    InternalSummonAicaDataPayload,
    InternalSummonUserType,
} from '../../../../../../../../store/user/actions';
import { AICA_DATA_PENDING, AICA_EDIT_DEAL } from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { updateQueryParams } from '../../../../../../../../utils/searchParamUtils';
import { fetchDataByConfig } from '../../../../../../../../common/ManageDealDrawer/ManageDealDrawerService';
import { polusBaseUrl } from '../../../../../../../../utils/axios-interceptor';
import LenderUserCell from '../LenderUsersCell/LenderUsersCell';
import ProbabilityBar from '../../../../../../../DebtProducts/components/ProbabilityBar/ProbabilityBar';
import DebtProductOverview from '../../../../../../../DebtProducts/components/DebtProductOverview/DebtProductOverview';
import { getDealProbabilityData } from '../../../../../../../../store/debtProducts/apiHelper_debtProducts';
import { DebtProductRowData } from '../../../../../../../DebtProducts/debtProductTypes';
import { DESIGNATION, SortConfig, VIEW_CONFIGS } from '../../../../../../../../utils/constants/commonConstants';
import { fallbackDealStatusOption } from '../../../DealsList';
import {
    CommentCellRenderer,
    CompanyNameCellRenderer,
    ComponentCellRenderer,
    DataTable,
    DataTableColumnType,
    DatePickerCellRenderer,
    ExpandableRowCellRenderer,
    GradientTextCellRenderer,
    LastStatusUpdateCellRenderer,
    MultiSelectDropdownCellRenderer,
    NumberCellRenderer,
    OwnerCellRenderer,
    SingleSelectDropdownCellRenderer,
} from '../../../../../../../../common/_custom';
import { DataTableColumn } from '../../../../../../../../common/_custom/DataTable/DataTable.types';
import { UpdateSingleSelectDropdownProps } from '../../../../../../../../common/_custom/DataTable/components/CellRenderers/SingleSelectDropdownCellRenderer';
import { handleRowClickProps } from '../../../../../../../../common/_custom/DataTable/components/CellRenderers/CompanyNameCellRenderer/CompanyNameCellRenderer';
import { UpdateOwnerProps } from '../../../../../../../../common/_custom/DataTable/components/CellRenderers/OwnerCellRenderer';
import { ModalActionCellRenderer } from '../../../../../../../../common/_custom/DataTable/components/CellRenderers/ModalActionCellRenderer/ModalActionCellRenderer';
import { ModalActionCellData, BorrowerRequirementsCount } from '../../../../../../../../common/_custom/DataTable/DataTable.types';
import { STYLE_CONSTANTS } from '../../../../../../../../common/_custom/DataTable/DataTable.constants';
import { renderFnProps } from '../../../../../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { DealUpdatePayload, InvestorDealUpdatePayload } from '../../../../../../../../store/investor/service';
import { useStatusTimeline } from '../../../../../../../../common/StatusTimelineDrawer/useStatusTimeline';
import StatusTimelineDrawer from '../../../../../../../../common/StatusTimelineDrawer/StatusTimelineDrawer';
import { processSortableHeaders } from '../../../../../../../../utils/sortHeaderUtils';
import { useVisibilityConfig } from '../../../../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../../../../utils/constants/visibilityKeys';
export type ActionMenuConst = {
    [x: string]: { action: Function };
};

type Props = {
    headers: any;
    rows: FetchDealsData[];
    selectedCategory?: string;
    loading: boolean;
    totalCount: number;
    emptyScreenComponent: React.ReactNode;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
    dealStatusConfig: null | DealStatusConfig;
    currentColumns?: string[];
    onColumnsChange?: (columns: DataTableColumn[]) => void;
};

type Option = {
    label: string;
    value: string;
    type?: string;
};

const DealsRow: React.FC<Props> = ({
    headers,
    rows,
    selectedCategory,
    loading,
    totalCount,
    emptyScreenComponent,
    sortConfig,
    onSort,
    dealStatusConfig,
    currentColumns = [],
    onColumnsChange,
}) => {
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const location = useLocation();
    const navigate = useNavigate();
    const partnerData = useAppSelector((state) => state.investor.partnerData);
    const { getFeatureVisibility } = useVisibilityConfig();
    const dealsFieldVisibilityKey =
        selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW
            ? VISIBILITY_KEYS.DATA_TABLE.DEALS_FIELD_UPDATE
            : VISIBILITY_KEYS.DATA_TABLE.MANDATE_FIELD_UPDATE;
    const dealsFieldVisibility = getFeatureVisibility(dealsFieldVisibilityKey);

    const getDealsFieldCellDisabled = useCallback(
        (columnDisable: boolean | undefined) => {
            if (dealsFieldVisibility.write) return false;
            if (dealsFieldVisibility.disabled) return true;
            return columnDisable || false;
        },
        [dealsFieldVisibility.write, dealsFieldVisibility.disabled],
    );

    // Status timeline functionality - only for MANDATE view, not for DEAL view
    const {
        isDrawerOpen: isStatusTimelineOpen,
        statusTimeline,
        loading: statusTimelineLoading,
        handleStatusTimelineClick,
        handleDrawerClose: handleStatusTimelineClose,
    } = useStatusTimeline({ recordType: 'MANDATE', baseURL: polusBaseUrl });

    const ACTION_MENU_ITEMS: ActionMenuConst = {
        'Edit Deal': {
            action: (payload, source) => {
                updateQueryParams(queryParams, setQueryParams, { dealStatus: payload?.dealStatus, location });
                const drawer = payload?.mandateState === DealStatus.DRAFT ? 'createDeal' : 'editDeal';
                editTerms(payload?.dealBeneficiaryId, payload?.borrowerName, payload?.dealId, 0, 'Edit Deal', drawer);
            },
        },
        'Edit Mandate': {
            action: (payload, source) => {
                updateQueryParams(queryParams, setQueryParams, { dealStatus: payload?.dealStatus, location });
                const drawer = payload?.mandateState === DealStatus.DRAFT ? 'createDeal' : 'editDeal';
                editTerms(payload?.dealBeneficiaryId, payload?.borrowerName, payload?.dealId, 0, 'Edit Mandate', drawer);
            },
        },
        'Add Lender': {
            action: ({ dealBeneficiaryId, id, dealId }) => {
                const resolvedDealId = id ?? dealId;
                navigate(`/investor/app/debt-products/${dealBeneficiaryId}?tab=debtProducts`, {
                    state: { mandateId: resolvedDealId },
                });
            },
        },
        'Deal Terms': {
            action: (payload) => {
                // For action menu, look for any column with restrictBackwardMovement
                const columnWithRestriction = headers?.columns?.find((header) => header.field === 'dealStatus');
                const restrictBackwardMovement = columnWithRestriction?.restrictBackwardMovement || false;

                setDealTermsQueryParams(
                    {
                        dealId: payload?.dealId,
                        borrowerName: payload?.borrowerName,
                        investorName: payload?.lenderName || '',
                        restrictBackwardMovement,
                    },
                    'Deal Terms',
                );
            },
        },
        'Borrower Profile': {
            action: (payload, source) => {
                if (isLenderType(userType) || source !== 'deal') {
                    viewLenderBorrowerProfile(payload?.dealBeneficiaryId, payload?.borrowerName, payload?.dealId);
                } else {
                    viewBorrowerProfile(payload?.dealBeneficiaryId, payload?.borrowerName, payload?.dealId);
                }
            },
        },
        'AICA Report': {
            action: (payload) => viewReport(payload?.dealBeneficiaryId),
        },
        'Download Data': {
            action: (payload) => exportData(payload?.dealBeneficiaryId, payload?.borrowerName),
        },
        'One Pager': {
            action: (payload) => {
                let onePagerDoc = payload?.documents?.find((doc) => doc.documentName?.includes(UPLOAD_FILES_PREFIXES.ONEPAGER_PREFIX));
                onePagerDoc?.docId &&
                    downloadFile(
                        Number(onePagerDoc.docId),
                        () => {},
                        onePagerDoc?.documentName?.replace?.(UPLOAD_FILES_PREFIXES.ONEPAGER_PREFIX, ''),
                    );
            },
        },
        'Investment Report': {
            action: (payload) => {
                let investmentReportDoc = payload?.documents?.find(
                    (doc) => doc.documentName?.includes(UPLOAD_FILES_PREFIXES.INVESTMENTREPORT__PREFIX),
                );
                investmentReportDoc?.docId &&
                    downloadFile(
                        Number(investmentReportDoc.docId),
                        () => {},
                        investmentReportDoc?.documentName?.replace?.(UPLOAD_FILES_PREFIXES.INVESTMENTREPORT__PREFIX, 'Investment_Report_'),
                    );
            },
        },
        'Compose Email': {
            action: (payload) => {
                dispatch(
                    setComposeEmailModal({
                        show: true,
                        orgName: payload?.lenderName,
                        orgId: payload?.dealReceiverId,
                        source: COMPOSE_EMAIL_SOURCE.DEALS_PAGE,
                        selectedRequirements: [],
                        dealId: payload?.dealId,
                    }),
                );
            },
        },
    };
    // Constants

    const dispatch = useAppDispatch();

    const [queryParams, setQueryParams] = useSearchParams();

    const loggerInUser = useAppSelector((state) => state.user.userData);
    const user = useAppSelector((state) => state.user);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const isPartner = user.userData.accountType === USER_TYPE.PARTNER;
    const isBorrower = user.userData.accountType === USER_TYPE.COMPANY;
    const isBorrowerSide = isBorrower;
    const [ownerDropdownData, setOwnerDropdownData] = useState({});
    const userType = user.userData.accountType;
    const [collapsedMap, setCollapsedMap] = useState<{ [key: string]: boolean }>({});
    const [probabilityDrawerData, setProbabilityDrawerData] = useState<{
        snapshots: any[];
        productName?: string;
        companyName?: string;
        isDealView: boolean;
        createdAt?: number;
        dealSharedBy?: string;
        lenderId?: string;
    } | null>(null);
    const aicaDebtProductConfig = useAppSelector((state) => state.investor.aicaDebtProductConfig);
    const platformUserId = getPlatformUserId();
    const manageInvestorsVisibility = getFeatureVisibility(VISIBILITY_KEYS.LENDER_MANAGEMENT.SUMMON);
    const shouldDisableLenderInteraction = () => {
        return !manageInvestorsVisibility.write;
    };

    const prepareOwnerDropdownData = () => {
        const ownersData = {};
        owners.forEach((owner) => {
            if (!owner.name) return;
            ownersData[owner.userId] = {
                label: owner?.name,
                value: owner.userId,
                backgroundColor: COLORS.LIGHT_BG,
                labelColor: COLORS.BLACK,
                customCaret: GREY_DROPDOWN_CARET,
            };
        });
        return ownersData;
    };

    const fetchLenderUsers = async (lenderId: string): Promise<Option[]> => {
        const payload = {
            orgId: lenderId,
            accountType: userData?.accountType,
        };

        const response = await fetchDataByConfig(payload, 'AICA_GET_ORG_USERS', 'GET', polusBaseUrl);

        if (response) {
            const res = response.data.responseBody.data || [];
            return res.map((user) => ({
                label: user.name,
                value: user.userId,
                type: user.userType === USER_TYPES.LENDER ? DESIGNATION.ADMIN : DESIGNATION.TEAM_MEMBER,
            }));
        }

        return [];
    };

    useEffect(() => {
        if (owners.length > 0) setOwnerDropdownData(prepareOwnerDropdownData());
    }, [owners]);

    useEffect(() => {
        handleRowCollapseFromParams();
    }, [queryParams]);

    const handleRowCollapseFromParams = () => {
        const rowId = queryParams.get('rowId');
        if (rowId) {
            setCollapsedMap((prev) => ({ ...prev, [rowId]: false }));
            queryParams.delete('rowId');
            setQueryParams(queryParams);
        }
    };

    const getStatusDropdownOption = (status: string, source: string): StatusDropdownOption => {
        return getStatusDropdownOptionUtil(
            status,
            dealStatusConfig,
            selectedCategory || DEAL_VIEW_TYPES.DEAL_VIEW,
            source,
            fallbackDealStatusOption,
        );
    };

    const getOwnerDropdownOption = (ownerId: string): any => {
        let option = Object.values(ownerDropdownData).find((owner: any) => owner.value === ownerId);
        if (option) return option;
    };

    const setDealTermsQueryParams = (
        payload: { dealId: string; borrowerName: string; investorName: string; restrictBackwardMovement?: boolean },
        source: string,
    ) => {
        queryParams.set('showDealTermsLoggingDrawer', 'true');
        queryParams.set('investorDealId', payload.dealId);
        queryParams.set('borrowerName', payload.borrowerName);
        queryParams.set('investorName', payload.investorName);
        queryParams.set('restrictBackwardMovement', String(payload.restrictBackwardMovement || false));
        queryParams.set('source', source);
        setQueryParams(queryParams);
    };

    const handleDealTermsClick = (row: FetchDealsData | InvestorDealData, columnField?: string) => {
        // Find column info for restrictBackwardMovement if columnField is provided
        let restrictBackwardMovement = false;
        const column = headers.columns?.find((header) => header.field === 'dealStatus');
        restrictBackwardMovement = column?.restrictBackwardMovement || false;
        setDealTermsQueryParams(
            {
                dealId: row?.dealId,
                borrowerName: row?.borrowerName,
                investorName: row?.lenderName || '',
                restrictBackwardMovement,
            },
            'View Deal',
        );
    };
    const handleLenderDataPendingClick = (row) => {
        queryParams.set(SOURCE.LENDER_REQ, 'true');
        queryParams.set('needInfo', 'true');
        queryParams.set('showNeedInfo', 'true');
        queryParams.set('hideMu', 'true');
        queryParams.set('assesseeId', row.dealBeneficiaryId);
        if ('dealReceiverId' in row) {
            queryParams.set('lenderId', row?.dealReceiverId);
        }
        queryParams.set('companyName', row.borrowerName);
        queryParams.set('lenderName', row.lenderName as string);
        queryParams.set('dealId', row.dealId);
        row?.id && queryParams.set('rowId', row?.id);
        setQueryParams(queryParams);

        Mixpanel.track(AICA_DATA_PENDING, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: user?.userData?.investorOrgId,
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            position: queryParams.get('type'),
            organizationId: row?.dealBeneficiaryId,
        });
    };

    const updateDealStatus = (dealId: string, status: DealStatus) => {
        dispatch(
            updateDeal({
                dealId,
                dealStatus: status as string,
                lastUpdatedBy: getPlatformUserId(),
                userId: user.userData.userId,
            }),
        );
    };

    const updateDealOwner = (dealInfo) => {
        dispatch(updateDeal(dealInfo));
    };

    const updateInvestorDealStatus = (dealId: string | undefined, investorDealId: string, status: DealStatus, source?: string) => {
        dispatch(
            updateInvestorDeal({
                dealId,
                investorDealId,
                dealStatus: status as string,
                lastUpdatedBy: getPlatformUserId(),
                source,
                selectedCategory,
                owner: {
                    name: user.userData.userName,
                    userId: user.userData.userId,
                    activityBy: user.userData.userName,
                    activityDate: new Date().toISOString(),
                    ownerType: user.userData.accountType,
                },
            }),
        );
    };

    /**
     * Helper function to perform the actual deal status update
     * This is extracted so it can be called both directly and from the modal's onSuccess callback
     */
    const performDealStatusUpdate = (row: any, newStatus: DealStatus, source: string) => {
        if (isLenderType(userType) || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) {
            updateInvestorDealStatus(undefined, row?.dealId, newStatus, userType);
        } else if (source === 'deal') {
            updateDealStatus(row?.dealId, newStatus);
        } else {
            updateInvestorDealStatus(row?.parentDealId ?? '', row?.dealId, newStatus);
        }
    };

    const summonLender = async (lenderId: string) => {
        if (!lenderId || !userData?.investorOrgId) return;

        try {
            const lenderPayload = {
                orgId: lenderId,
                accountType: userData?.accountType,
            };
            const response = await fetchDataByConfig(lenderPayload, 'AICA_GET_ORG_USERS', 'GET', polusBaseUrl);

            let userId = response?.data?.responseBody?.data?.[0]?.userId;

            const platformUserId = getPlatformUserId();
            const parentOrgId = platformData?.investorOrgId || '';
            const parentEmailId = userData?.investorPrimaryEmail;

            const payload: InternalSummonAicaDataPayload = {
                parentOrgId,
                parentEmailId,
                userType: InternalSummonUserType.LENDER,
                requesterUserId: platformUserId,
                userId,
            };

            dispatch(
                fetchInternalSummonAicaData(payload, {
                    onSuccess: (userData) => {
                        if (userData) {
                            openUserSummonTab({ ...userData, orgId: parentOrgId });
                        }
                    },
                }),
            );
        } catch (error) {
            console.error('Failed to summon lender:', error);
        }
    };

    const onRowClick = (event: React.MouseEvent<HTMLTableRowElement | HTMLButtonElement, MouseEvent>, row: any) => {
        event.stopPropagation();
        if (isLenderType(userType)) viewReport(row.dealBeneficiaryId);
        if (selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW && !row?._isCollapsibleRow) {
            handleStatusTimelineClick(row);
            return;
        }
        if ((selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW || row?._isCollapsibleRow) && row?.dealId) handleDealTermsClick(row);
        setCollapsedMap({ ...collapsedMap, [row.dealId]: !(collapsedMap[row.dealId] ?? true) });
    };

    const updateLenderUsers = (selectedUserIds: string[], options: Option[], dealId) => {
        const selectedUsers = options
            .filter((opt) => selectedUserIds.includes(opt.value) && opt.type !== DESIGNATION.ADMIN) // filter out Admins
            .map((opt) => ({
                name: opt.label,
                userId: opt.value,
            }));

        const dealTerms = {
            lenderUsers: {
                userIds: selectedUsers,
                type: selectedUsers.length === 0 ? 'ALL' : 'SELECTED',
            },
        };
        dispatch(
            updateInvestorDeal({
                lastUpdatedBy: getPlatformUserId(),
                dealTerms,
                investorDealId: dealId,
                selectedCategory,
                selectedLenderUsers: selectedUserIds,
                allUserOptions: options,
                owner: {
                    name: user.userData.userName,
                    userId: user.userData.userId,
                    activityBy: user.userData.userName,
                    activityDate: new Date().toISOString(),
                    ownerType: user.userData.accountType,
                },
                isLenderSideUsers: isLenderType(userType),
            }),
        );
    };

    const viewReport = (id: string) => {
        const url = `/investor/app/portfolio/${id}?${queryParams.toString()}`;
        window.open(url, '_blank');
    };

    const viewDataRoom = (id: string) => {
        const url = `/investor/app/data-room/${id}?${queryParams.toString()}`;
        window.open(url, '_blank');
    };
    const editTerms = (companyId, companyName, dealId, step = 0, heading = 'Edit Deal', source = 'editDeal') => {
        dispatch(
            handleDealApplicationDrawer({
                open: true,
                dealId: dealId,
                dealBeneficiaryId: companyId,
                heading: heading,
                subHeading: companyName,
                source: source,
                width: '67%',
                step: step,
            }),
        );
        queryParams.set('dealCreated', 'true');
        setQueryParams(queryParams);

        Mixpanel.track(AICA_EDIT_DEAL, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: user?.userData?.investorOrgId,
            userId: user?.userData?.userId,
            userEmail: user?.userData?.primaryEmail,
            currentUrl: window.location.href,
            organizationId: companyId,
            companyName: companyName,
        });
    };

    const viewBorrowerProfile = (companyId: string, companyName: string, dealId: string) => {
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: 'Borrower Profile',
                companyId,
                companyName,
                dealId,
                readonly: true,
                step: 'BORROWER_PROFILE',
            }),
        );
    };

    const viewLenderBorrowerProfile = (companyId: string, companyName: string, dealId: string) => {
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: 'Borrower Profile',
                companyId,
                companyName,
                lenderDealId: dealId,
                readonly: true,
                step: 'BORROWER_PROFILE',
            }),
        );
    };

    const exportData = (orgId: string, orgName: string) => {
        exportRawFromUnderwritingAndPolus(
            {
                assesseeOrgId: orgId,
                orgName,
            },
            {},
        );
    };

    const updateCustomFieldStatus = (row, option, column, handlers) => {
        const source = row?._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW ? 'investor-deal' : 'deal';
        const customFieldKey = column?.field.split('.')[1];
        if (!customFieldKey) return;

        // Support both single values and arrays (for multi-select dropdowns)
        const fieldValue = option.value;

        if (source === 'investor-deal') {
            dispatch(
                updateInvestorDeal(
                    {
                        dealId: row?.parentDealId ?? '',
                        investorDealId: row?.dealId,
                        customFields: {
                            [customFieldKey]: fieldValue,
                        },
                        lastUpdatedBy: getPlatformUserId(),
                        source,
                        selectedCategory,
                        owner: {
                            name: user.userData.userName,
                            userId: user.userData.userId,
                            activityBy: user.userData.userName,
                            activityDate: new Date().toISOString(),
                            ownerType: user.userData.accountType,
                        },
                    },
                    handlers,
                ),
            );
        } else {
            dispatch(
                updateDeal(
                    {
                        dealId: row?.dealId,
                        customFields: {
                            [customFieldKey]: fieldValue,
                        },
                        lastUpdatedBy: getPlatformUserId(),
                        userId: platformUserId,
                    },
                    handlers,
                ),
            );
        }
    };

    /**
     * A registry function that maps column fields to custom React components.
     *
     * This function is passed into a generic cell renderer (`ComponentCellRenderer`)
     * and is responsible for rendering the appropriate component for each column field
     * based on the row and column context.
     */
    const componentRenderFnRegistry = ({ row, column }: renderFnProps) => {
        switch (column?.field) {
            case DEAL_COLUMN_KEYS.ACTIONS:
                return (
                    <ActionMenu
                        triggerPayload={row}
                        actionMenu={ACTION_MENU_ITEMS}
                        rowData={row}
                        source={row._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW ? 'investor-deal' : 'deal'}
                        disabled={!row?.dealId && row?._isCollapsibleRow}
                        excludedActions={
                            row.mandateState === 'DRAFT'
                                ? ['AICA Report', 'Download Raw Data', ...(isLenderType(userType) ? [] : ['Deal Terms'])]
                                : isLenderType(userType)
                                ? ['Compose Email']
                                : ['Deal Terms']
                        }
                        usePortal={true}
                        style={{ left: '88%' }}
                    />
                );
            case DEAL_COLUMN_KEYS.LENDER_USERS: {
                if (!row?.['lenderUsers']) return <div style={{ color: COLORS.GREY }}>-</div>;
                return (
                    <LenderUserCell
                        users={row?.['lenderUsers'] || []}
                        dealId={row?.dealId}
                        updateLenderUsers={updateLenderUsers}
                        fetchLenderUsers={fetchLenderUsers}
                        dealReceiverId={'dealReceiverId' in row ? row?.dealReceiverId : ''}
                        dealStatus={row?.dealStatus}
                    />
                );
            }
            case DEAL_COLUMN_KEYS.DEAL_PROBABILITY: {
                const probability = row?.customFields?.debtOptionProbability;
                const pecThreshold = row?.customFields?.probabilityPecThreshold;
                const hasValue = probability != null && probability > 0;
                const isDealViewRow = row?._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
                const handleProbabilityClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!hasValue || !row?.dealId) return;
                    if (!aicaDebtProductConfig?.debtProductOverviewTableHeaders) {
                        dispatch(
                            fetchLenderConfig({
                                accountType: userData.accountType,
                                investorOrgId: userData.investorOrgId,
                                primaryEmail: userData.primaryEmail,
                                assesseeOrgId: row.dealBeneficiaryId,
                                userRole: userData?.roles?.[0],
                                configName: VIEW_CONFIGS.AICA_DEBT_PRODUCT,
                            }),
                        );
                    }
                    const dealCreatorId = row.owners?.dealCreator;
                    const dealSharedByName = owners.find((o) => o.userId === dealCreatorId)?.name;
                    getDealProbabilityData(row.dealId, {
                        onSuccess: (snapshots) =>
                            setProbabilityDrawerData({
                                snapshots,
                                productName: row.loanTypes?.[0],
                                companyName: row.borrowerName,
                                createdAt: row.createdAt,
                                dealSharedBy: dealSharedByName,
                                isDealView: isDealViewRow,
                                lenderId: isDealViewRow ? (row as any)?.dealReceiverId : undefined,
                            }),
                    });
                };
                return (
                    <div
                        className={clsx(styles.ProbabilityRow, { [styles.Clickable]: hasValue, [styles.Disabled]: !hasValue })}
                        onClick={handleProbabilityClick}
                    >
                        <ProbabilityBar
                            probability={String(probability ?? 0)}
                            pecThreshold={!isPartner && pecThreshold != null ? String(pecThreshold) : undefined}
                        />
                        {hasValue && <img src={ctaGrey} alt="" height="12px" style={{ flexShrink: 0 }} />}
                    </div>
                );
            }
            default:
                return null;
        }
    };

    const partnerDropdownOptions = useMemo(() => {
        if (!partnerData || !Array.isArray(partnerData)) return {};
        const filteredOrgs = partnerData.filter((org) => org?.organizationId && org?.organizationDisplayName);
        const params = filteredOrgs.map((org) => ({
            organizationId: org?.organizationId || '',
            organizationDisplayName: org?.organizationDisplayName || '',
        }));
        return createDropdownOptions(params);
    }, [partnerData]);

    const podUserDropdownOptions = useMemo(() => {
        if (!owners || !Array.isArray(owners)) return {};
        const filteredUsers = owners.filter((user) => user?.userId && user?.name);
        const params = filteredUsers.map((user) => ({
            organizationId: user?.userId || '',
            organizationDisplayName: user?.name || '',
        }));
        return createDropdownOptions(params);
    }, [owners]);

    /**
     * Configuration map for single-select dropdown cell types.
     * Each key defines how options are generated, selected, and updated based on the column field.
     */
    const singleSelectDropdownConfig = {
        dealStatus: {
            getOptions: (row: any) => {
                const isInvestorDealView = row?._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;

                let dropdownOptions = isInvestorDealView ? dealStatusConfig?.[DEAL] || [] : dealStatusConfig?.[MANDATE] || [];
                if (isInvestorDealView) {
                    dropdownOptions = dropdownOptions.filter((option) => option.value !== DealStatus.INTERESTED);
                } else {
                    dropdownOptions = dropdownOptions.filter((option) => option.value !== DealStatus.DRAFT);
                }

                return dropdownOptions;
            },
            getSelected: (row: any) => {
                const isInvestorDeal = row?._isCollapsibleRow;
                const source = isInvestorDeal ? 'investor-deal' : 'deal';
                if (!row?.dealId) return fallbackDealStatusOption['Default'];
                return getStatusDropdownOption(row?.dealStatus, source);
            },
            onUpdate: ({ row, option }: UpdateSingleSelectDropdownProps) => {
                const source = row?._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW ? 'investor-deal' : 'deal';
                if (option.value === row?.dealStatus) return;

                // For lender type, directly update without showing modal
                if (isLenderType(userType)) {
                    updateInvestorDealStatus(undefined, row?.dealId, option.value as DealStatus, userType);
                    return;
                }

                // For investor-deal, skip modal and directly open DealTermsLoggingDrawer with prefilled status
                if (source === 'investor-deal') {
                    // Set prefilled status in Redux
                    dispatch(setDealTermsLoggingDrawerPrefilledStatus(option.value as string));

                    // Find column info for restrictBackwardMovement
                    const column = headers.columns?.find((header) => header.field === 'dealStatus');
                    const restrictBackwardMovement = column?.restrictBackwardMovement || false;

                    // Open the drawer
                    setDealTermsQueryParams(
                        {
                            dealId: row?.dealId,
                            borrowerName: row?.borrowerName,
                            investorName: row?.lenderName || '',
                            restrictBackwardMovement,
                        },
                        'Status Update',
                    );
                } else {
                    // For mandate/deal view, show the modal as before
                    const entity = ENTITY_TYPES.MANDATE;
                    const actionType = 'dealStatus';

                    dispatch(
                        setConfigTableModal({
                            show: true,
                            data: {
                                entity,
                                actionType,
                                dropdownOption: option.value as string,
                                rowData: row,
                                source: [ENTITY_TYPES.LEAD, entity], // Save to both Lead and Deal/Mandate when modal is shown
                                dealId: row?.parentDealId,
                                orgId: row?.dealBeneficiaryId, // Pass orgId for Lead data fetching
                                mandateId: row?.dealId,
                                onSuccess: () => {
                                    performDealStatusUpdate(row, option.value as DealStatus, source);
                                },
                            },
                        }),
                    );
                }
            },
        },
        customFields: {
            getOptions: (row: any, column: any) => {
                if (column?.field === 'customFields.partnerOrgId') {
                    return partnerDropdownOptions;
                }
                if (column?.field === 'customFields.podUserId') {
                    return podUserDropdownOptions;
                }
                return row?._isCollapsibleRow ? column?.collapsedDropdownOptions : null;
            },
            getSelected: (row: any) => {
                return null;
            },
            onUpdate: ({ row, option, column }: UpdateSingleSelectDropdownProps) => {
                updateCustomFieldStatus(row, option, column, {});
            },
        },
    };

    const handleNameClick = ({ event, row, column }: handleRowClickProps) => {
        event?.stopPropagation();
        if (!row || !column?.field) return;

        const rawField = column?.field;
        const actualField = rawField === 'borrowerName' && row._isCollapsibleRow ? 'lenderName' : rawField;

        const actions: Record<string, () => void> = {
            borrowerName: () => {
                if (isPartner) {
                    queryParams.set('source', 'deals');
                    setQueryParams(queryParams);
                    viewDataRoom(row.dealBeneficiaryId);
                } else {
                    viewReport(row.dealBeneficiaryId);
                }
            },
            lenderName: () => {
                // Don't allow clicking on lender name for certain user types
                if (shouldDisableLenderInteraction()) return;
                summonLender(row.dealReceiverId as string);
            },
        };

        actions[actualField]?.(); // Only invoke if the field matches a key
    };

    const updateOwner = ({ row, label, value, headerId }: UpdateOwnerProps) => {
        // Find the owner object to get customSortName
        const selectedOwner = owners.find((owner) => owner.userId === value);
        const ownerName = selectedOwner?.customSortName || selectedOwner?.name || (label as string);

        const payload: DealUpdatePayload | InvestorDealUpdatePayload = {
            dealId: row?.dealId,
            owner: {
                name: ownerName,
                userId: value as string,
                activityBy: user.userData.userName,
                activityDate: new Date().toISOString(),
                ownerType: headerId as string,
            },
            lastUpdatedBy: getPlatformUserId(),
            userId: platformUserId,
        };
        if (selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW || row?._isCollapsibleRow) {
            payload.dealId = row?.id;
            payload.investorDealId = row?.dealId;
            payload.selectedCategory = selectedCategory;
            dispatch(updateInvestorDeal(payload as InvestorDealUpdatePayload));
        } else {
            updateDealOwner(payload as DealUpdatePayload);
        }
    };

    const computeLenderDataPendingData = (row: any): ModalActionCellData => {
        const dealStatusCounts: BorrowerRequirementsCount = row?.dealStatusCounts || {};
        const { pending, total, complete } = dealStatusCounts;

        let statusLabel: string;
        let isResolved: boolean;

        if (pending > 0) {
            statusLabel = `${pending} ${DEAL_STATUS_COUNT_TYPES.PENDING}`;
            isResolved = false;
        } else if (total === complete && total > 0) {
            statusLabel = `${DEAL_STATUS_COUNT_TYPES.RESOLVED}`;
            isResolved = true;
        } else {
            statusLabel = `${DEAL_STATUS_COUNT_TYPES.NONE}`;
            isResolved = true;
        }

        const colors = isResolved ? STYLE_CONSTANTS.COLORS.RESOLVED : STYLE_CONSTANTS.COLORS.PENDING;

        return {
            statusLabel,
            hoverBgColor: colors.HOVER_BG,
            textColor: colors.TEXT,
            iconFill: colors.ICON_FILL,
            hoverFill: colors.HOVER_FILL,
            hoverTextColor: colors.HOVER_TEXT,
        };
    };

    /**
     * Configuration for modal action properties (icon, handler, and data computation) based on field name.
     */
    const modalActionConfig = {
        dealTerms: {
            icon: TopRightArrowIcon,
            handler: handleDealTermsClick,
        },
        lenderDataPending: {
            icon: TopRightArrowIcon,
            handler: handleLenderDataPendingClick,
            computeData: computeLenderDataPendingData,
        },
    };

    /**
     * Flattens the rows by expanding investorDeals as sub-rows if the row is not collapsed.
     *
     * @typedef {Object} Row
     * @property {string} dealId - Unique identifier for the row
     * @property {Array<Object>} [investorDeals] - Optional array of investor sub-rows
     * @property {boolean} [_isCollapsibleRow] - Flag to identify if this is an expanded sub-row
     * @property {string} [parentDealId] - Parent deal ID for sub-rows
     * @property {Object} [rowStyle] - Optional inline styles for the row
     *
     * @type {Row[]}
     */
    const flattenedRows = useMemo(
        () =>
            rows?.flatMap((row) => {
                const dealId = row?.dealId;
                const isCollapsed = collapsedMap?.[dealId] ?? true;

                const parentRow = {
                    ...row,
                    _isCollapsibleRow: false,
                    rowStyle: { height: '44px' },
                };

                const expandedRows =
                    !isCollapsed && Array.isArray(row?.investorDeals)
                        ? row.investorDeals.map((investorRow) => ({
                              ...investorRow,
                              _isCollapsibleRow: true,
                              parentDealId: dealId,
                              rowStyle: { backgroundColor: '#f9fafc', height: '44px' },
                          }))
                        : [];

                return [parentRow, ...expandedRows];
            }),
        [rows, collapsedMap],
    );

    const getConfigKey = (field: string = ''): string => {
        if (!field) return '';
        return field.includes('customFields') ? 'customFields' : field;
    };

    // Process headers to add sort icons for sortable columns
    // Note: Column filtering by show property is now handled by DataTable component
    const processedHeaders = useMemo(() => {
        return processSortableHeaders({
            headers: headers,
            sortConfig,
            onSort,
            selectedCategory,
            dealViewTypes: DEAL_VIEW_TYPES,
        });
    }, [headers, sortConfig, onSort, selectedCategory]);
    const handleFiltersApply = (filters: Record<string, any>) => {
        // Deep clone the filters to prevent state mutations
        const clonedFilters = JSON.parse(JSON.stringify(filters));

        // Use the same sourceKey logic as in useDealsList to ensure consistency
        const sourceKey = selectedCategory === 'DEAL_VIEW' || selectedCategory === 'MANDATE_VIEW' ? 'DEALS' : selectedCategory || 'DEALS';

        dispatch(
            setCustomFilterDrawer({
                open: false,
                appliedFilters: {
                    [sourceKey]: clonedFilters,
                },
            }),
        );

        // Mark filters as modified to prevent view re-initialization
        if ((window as any).markViewAsModified) {
            (window as any).markViewAsModified();
        }
    };

    return (
        <>
            <DataTable
                columns={processedHeaders.columns}
                rows={flattenedRows}
                onRowClick={onRowClick}
                loading={loading}
                emptyState={emptyScreenComponent}
                totalRowCount={totalCount}
                customCellRenderers={{
                    [DataTableColumnType.COMPANY_NAME_CTA]: (props) => {
                        // Disable lender name cell for certain user types
                        const isLenderNameDisabled =
                            props.column?.field === 'borrowerName' && props.row?._isCollapsibleRow && shouldDisableLenderInteraction();

                        return (
                            <CompanyNameCellRenderer
                                {...props}
                                dynamicProps={{
                                    handleRowClick: handleNameClick,
                                    isDisabled: isLenderNameDisabled,
                                }}
                            />
                        );
                    },
                    [DataTableColumnType.OWNER]: (props) => (
                        <OwnerCellRenderer
                            {...props}
                            dynamicProps={{
                                getOwnerDropdownOption,
                                updateOwner,
                                isDisabled: !props?.row?.dealId || getDealsFieldCellDisabled(props?.column?.disable),
                            }}
                        />
                    ),
                    [DataTableColumnType.SINGLE_SELECT_DROPDOWN]: (props) => (
                        <SingleSelectDropdownCellRenderer
                            {...props}
                            dynamicProps={{
                                getStatusDropdownList: (column: any, row: any) =>
                                    singleSelectDropdownConfig[getConfigKey(column?.field)]?.getOptions?.(row, column),

                                setSelectedDropdownOption: (row?: any, column?: any) =>
                                    singleSelectDropdownConfig[getConfigKey(column?.field)]?.getSelected?.(row),

                                updateSingleSelectDropdown: (params: UpdateSingleSelectDropdownProps) =>
                                    singleSelectDropdownConfig[getConfigKey(params.column?.field)]?.onUpdate?.(params),

                                isDisabled: !props?.row?.dealId || getDealsFieldCellDisabled(props?.column?.disable),
                            }}
                        />
                    ),
                    [DataTableColumnType.GRADIENT_TEXT]: (props) => <GradientTextCellRenderer {...props} />,
                    [DataTableColumnType.COMPONENT]: (props) => (
                        <ComponentCellRenderer
                            {...props}
                            dynamicProps={{
                                componentRenderFnRegistry,
                            }}
                        />
                    ),
                    [DataTableColumnType.COMMENT]: (props) => (
                        <CommentCellRenderer
                            {...props}
                            dynamicProps={{
                                source:
                                    props.row._isCollapsibleRow || selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW
                                        ? 'investor-deal'
                                        : 'deal',
                                getOrgId: (row) => row.dealBeneficiaryId,
                                isDealView: selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW,
                            }}
                        />
                    ),
                    [DataTableColumnType.MODAL_ACTION]: (props) => {
                        const fieldConfig = modalActionConfig[props.column?.field] ?? {
                            icon: null,
                            handler: () => {},
                        };

                        const { icon: Icon, handler: handleModalActionClick, computeData } = fieldConfig;

                        return (
                            <ModalActionCellRenderer
                                {...props}
                                dynamicProps={{
                                    handleModalActionClick,
                                    Icon,
                                    ...(computeData?.(props.row) || {}),
                                    disable: props?.row?.mandateState === DealStatus.DRAFT || false,
                                }}
                            />
                        );
                    },
                    [DataTableColumnType.EXPANDABLE]: (props) => (
                        <ExpandableRowCellRenderer
                            {...props}
                            dynamicProps={{
                                isCollapsed: collapsedMap[props.row.dealId] ?? true,
                                toggleCollapse: (row) => {
                                    setCollapsedMap((prev) => ({
                                        ...prev,
                                        [row.dealId]: !(prev[row.dealId] ?? true),
                                    }));
                                },
                            }}
                        />
                    ),
                    [DataTableColumnType.NUMBER]: (props) => (
                        <NumberCellRenderer
                            {...props}
                            dynamicProps={{
                                updateCustomFieldStatus,
                                isDisabled:
                                    !props?.row?.dealId ||
                                    (getDealsFieldCellDisabled(props?.column?.disable) && !props?.row?._isCollapsibleRow),
                            }}
                        />
                    ),
                    [DataTableColumnType.DATE_PICKER]: (props) => (
                        <DatePickerCellRenderer
                            {...props}
                            dynamicProps={{
                                updateCustomFieldStatus,
                                isDisabled: !props?.row?.dealId || false,
                            }}
                        />
                    ),
                    [DataTableColumnType.MULTI_SELECT_DROPDOWN]: (props) => (
                        <MultiSelectDropdownCellRenderer
                            {...props}
                            dynamicProps={{
                                updateMultiSelectDropdown: ({ row, column, selectedValues }) => {
                                    updateCustomFieldStatus(row, { value: selectedValues }, column, {});
                                },
                                isDisabled: !props?.row?.dealId || getDealsFieldCellDisabled(props?.column?.disable),
                            }}
                        />
                    ),
                    ...(selectedCategory !== DEAL_VIEW_TYPES.DEAL_VIEW && {
                        [DataTableColumnType.LAST_STATUS_UPDATE]: (props) => (
                            <LastStatusUpdateCellRenderer
                                {...props}
                                dynamicProps={{
                                    onStatusTimelineClick: handleStatusTimelineClick,
                                }}
                            />
                        ),
                    }),
                }}
                valueGetterMap={{
                    borrowerName: (row) => {
                        if (row._isCollapsibleRow) {
                            // Hide lender name for certain user types
                            return isBorrower ? '-' : row.lenderName;
                        }
                        return row.borrowerName;
                    },
                }}
                pinnedColumns={headers.pinnedColumns}
                onFiltersApply={handleFiltersApply}
                filterSourceKey="DEALS"
                enableColumnCustomization={true}
                masterColumnConfig={processedHeaders.columns}
                onColumnsChange={onColumnsChange}
            />

            {selectedCategory !== DEAL_VIEW_TYPES.DEAL_VIEW && (
                <StatusTimelineDrawer
                    isOpen={isStatusTimelineOpen}
                    onClose={handleStatusTimelineClose}
                    statusTimeline={statusTimeline}
                    loading={statusTimelineLoading}
                />
            )}
            {probabilityDrawerData && (
                <DebtProductOverview
                    data={probabilityDrawerData as unknown as DebtProductRowData}
                    closeModal={() => setProbabilityDrawerData(null)}
                    isDealView={probabilityDrawerData?.isDealView ?? true}
                    snapshots={probabilityDrawerData?.snapshots}
                    companyName={probabilityDrawerData?.companyName}
                    productName={probabilityDrawerData?.productName}
                    dealSharedInfo={
                        probabilityDrawerData?.createdAt && probabilityDrawerData?.dealSharedBy
                            ? { sharedAt: probabilityDrawerData.createdAt, sharedBy: probabilityDrawerData.dealSharedBy }
                            : undefined
                    }
                />
            )}
        </>
    );
};

export default DealsRow;
